// src/pages/Comercial/Produtos/ComercialProdutos.tsx
import React, { useMemo, useState, useCallback } from "react";
import Table from "../../../components/Table";
import TitleButtons from "../../../components/TitleButtons";
import { useNavigate } from "react-router-dom";
import { useProdutosDisponiveis } from "./ProdutoData";

const ComercialProdutos: React.FC = () => {
  const navigate = useNavigate();
  const { produtos, loading } = useProdutosDisponiveis();

  // Formatador que respeita a moeda vinda da API (moeda: "BRL", "USD", etc.)
  const formatCurrency = (v: unknown, currency: string | null | undefined) => {
    if (v === null || v === undefined || v === "") return "—";

    const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
    if (!Number.isFinite(n)) return "—";

    // Normaliza a moeda: BRL / USD / etc.
    const cur =
      typeof currency === "string" && currency.trim()
        ? currency.toUpperCase()
        : "BRL";

    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: cur,
      }).format(n);
    } catch {
      // Se vier uma moeda inválida, cai no fallback BRL
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(n);
    }
  };

  // Cabeçalhos (sem Custo de Compra e sem Margem)
  const headers = useMemo(
    () => ["Código", "NCM", "Nome do Produto", "Quantidade", "Preço de Venda"],
    []
  );

  const columnWidths = useMemo(
    () => ["100px", "60px", "270px", "80px", "120px"],
    []
  );

  // Linhas
  const rows = useMemo(
    () =>
      produtos.map((p) => {
        const isPendente =
          p.preco_venda != null &&
          p.custo_compra != null &&
          p.preco_venda === p.custo_compra;

        const precoCell = isPendente
          ? "Pendente"
          : formatCurrency(p.preco_venda, p.moeda);

        return [p.codigo, p.ncm, p.nome, p.quantidade ?? "—", precoCell];
      }),
    [produtos]
  );

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  // Helpers PDF (se for reativar o relatório depois)
  const firstCode = rows.length ? rows[0][0] : "—";
  const lastCode = rows.length ? rows[rows.length - 1][0] : "—";
  const nextCode = `PRD001${(produtos.length + 1).toString().padStart(2, "0")}`;

  // ========= Estilo condicional por margem =========
  const getCellStyle = useCallback(
    (
      _rowIndex: number,
      _colIndex: number,
      _value: string | number | React.ReactNode,
      originalIndex: number
    ): React.CSSProperties | undefined => {
      const produto = produtos[originalIndex];
      if (!produto) return;

      const m = produto.margem_percentual;

      // margem = 0 → vermelho pastel
      if (m === 0) {
        return {
          backgroundColor: "#EC525214", // vermelho pastel
        };
      }

      // margem > 0 → verde pastel
      if (m != null && m > 0) {
        return {
          backgroundColor: "#FFFFFF86", // verde pastel
        };
      }

      // sem margem definida → usa zebra padrão da tabela
      return;
    },
    [produtos]
  );

  return (
    <div>
      <TitleButtons title="Comercial - Produtos" buttons={[]} />

      <Table
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        enableCellColors
        getCellStyle={getCellStyle}
        onDoubleClick={(_viewIndex, rowData) => {
          const codigoIdx = headers.indexOf("Código");
          const codigo = String(rowData[codigoIdx] ?? "");
          if (codigo) {
            navigate(`/comercial/produtos/${encodeURIComponent(codigo)}`);
          }
        }}
        responsiveWidths={{
          1300: {
            1: "100px", // Código
            2: "60px", // NCM
            3: "260px", // Nome
            4: "80px", // Quantidade
            5: "120px", // Preço de Venda
          },
          1200: {
            1: "100px",
            2: "60px",
            3: "200px",
            4: "0px", // Quantidade oculta em telas pequenas
            5: "120px",
          },
        }}
      />

      {loading && (
        <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
          Carregando produtos…
        </div>
      )}
    </div>
  );
};

export default ComercialProdutos;
